import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { Award, Loader2, RotateCcw, Users } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { RankingCard, SectionHeader } from "@/components/marketing/common";

export function LoyaltyCustomersRanking({ customers, onPointsReset }) {
  const { authAxios } = useAuth();
  const [resettingPhone, setResettingPhone] = useState(null);

  const resetPoints = async (phone) => {
    setResettingPhone(phone);
    try {
      await authAxios.post(`/loyalty/reset-points/${encodeURIComponent(phone)}`);
      toast.success("Pontos resetados");
      onPointsReset(phone);
    } catch {
      toast.error("Erro ao resetar pontos");
    } finally {
      setResettingPhone(null);
    }
  };

  if (customers.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-sm text-muted-foreground">Nenhum cliente com pontos ainda. Eles aparecerão aqui após fazerem pedidos.</p>
      </div>
    );
  }

  return (
    <>
      <SectionHeader
        title="Ranking de Clientes"
        subtitle="Seus clientes mais fiéis"
      />

      <RankingCard
        title={`Clientes Mais Ativos (${customers.length})`}
        items={customers.slice(0, 10).map((c) => ({
          name: c.name || c.phone || "Cliente",
          value: c.points || 0,
          max: customers[0]?.points || 100,
        }))}
        formatValue={(v) => `${v.toLocaleString()} pts`}
      />

      <Card>
        <CardHeader className="pb-3 bg-slate-50 border-b">
          <CardTitle className="text-sm uppercase flex items-center gap-2">
            <span className="bg-blue-100 text-blue-600 p-2 rounded-md">
              <Users className="w-4 h-4" />
            </span>
            Histórico Detalhado
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="space-y-2">
            {customers.slice(0, 15).map((c, i) => (
              <div key={c.phone || i} className="flex items-center justify-between p-3 rounded-lg hover:bg-slate-50 border border-border/50 transition">
                <div className="flex items-center gap-3 flex-1">
                  <span className={`text-sm font-bold w-6 text-center ${i < 3 ? "text-amber-600" : "text-gray-400"}`}>{i + 1}</span>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-sm">{c.name || c.phone}</p>
                    <p className="text-xs text-muted-foreground">{c.total_orders || 0} pedidos • €{(c.total_spent || 0).toFixed(2)}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Badge className="bg-amber-100 text-amber-700">
                    <Award className="w-3 h-3 mr-1" />
                    {c.points || 0} pts
                  </Badge>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0 text-gray-400 hover:text-red-500"
                    title="Resetar pontos"
                    onClick={() => resetPoints(c.phone)}
                    disabled={resettingPhone === c.phone}
                  >
                    {resettingPhone === c.phone ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </>
  );
}
